import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { enabledForEventOrganizedAs } from 'app/shared/utils/events';
import { EventAttendee } from 'app/shared/typings';
import {
  AuthFormContextProvider,
  MultiStepSignUpContextProvider,
} from 'app/shared/context';
import { AuthContext } from 'app/shared/context/Auth';
import { Responsive } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { SmsSignUpForm } from 'app/shared/components/organisms';
import { shouldDisplayAdditionalPurchaseConfirmationModal } from 'app/fan/utils/checkout';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { Drawer } from 'app/fan/components/molecules';
import { AdditionalPurchaseConfirmationModal } from 'app/fan/components/organisms';
import { EventPageTemplate } from 'app/fan/components/templates';
import {
  AttendeeDetailsForm,
  CheckoutWidget,
  EventCTAWidget,
} from 'app/fan/components/pages/Event/components';
import { useTrackProductViewed } from 'app/fan/components/pages/Event/eventUtils';

interface Props {
  'data-qaid'?: string;
  eventAttendee: EventAttendee;
}

export const BuyFlowLoggedInPostPurchaseContainer = ({
  'data-qaid': dataQaid = 'event-page-buy-flow-post-purchase',
  eventAttendee,
}: Props) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const { event } = useEventPageContext();
  const { formatMessage } = useIntl();
  const { colors } = useCurrentTheme();
  const [confirmedAdditionalPurchase, setConfirmedAdditionalPurchase] =
    useState(false);
  const [
    displayAdditionalPurchaseConfirmationModal,
    setDisplayAdditionalPurchaseConfirmationModal,
  ] = useState(false);
  const [displayCheckoutSection, setDisplayCheckoutSection] = useState(false);
  const { trackAnalyticsEvent } = useAnalyticsContext();

  useTrackProductViewed({ eventAttendee });

  function openCheckoutDrawer() {
    if (
      shouldDisplayAdditionalPurchaseConfirmationModal(
        loggedInUser?.id,
        event?.id
      ) &&
      !confirmedAdditionalPurchase
    ) {
      trackAnalyticsEvent('Purchase Confirmation Modal Displayed', {});
      setDisplayAdditionalPurchaseConfirmationModal(true);
      setConfirmedAdditionalPurchase(true);
    } else {
      trackAnalyticsEvent('Checkout Drawer Opened', {
        user_has_tickets: false,
        event_id: event?.id,
      });
      setDisplayCheckoutSection(true);
    }
  }

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect these to be present
  if (!event || !loggedInUser) {
    return null;
  }

  const ticketsBought = eventAttendee.tickets.length;
  const maxTicketsBought = ticketsBought >= event.maxTicketsPerAttendee;

  function onDrawerClose() {
    trackAnalyticsEvent('Checkout Drawer Closed', {
      user_has_tickets: true,
      event_id: event?.id,
    });
  }

  const isOutOfStock = !!event.isSoldOut || !!event.cancelled;

  const canPurchaseMoreTickets =
    !maxTicketsBought && !!event.isAvailable && !isOutOfStock;

  return (
    <EventPageTemplate
      data-qaid={dataQaid}
      event={event}
      isOutOfStock={isOutOfStock}
      attendeeState={eventAttendee.currentState}
      numTickets={ticketsBought}
      secondColumn={
        <CheckoutWidget
          alreadyConfirmedAdditionalPurchase={confirmedAdditionalPurchase}
          allowPromoCode={false}
          disabled={!canPurchaseMoreTickets}
        />
      }
    >
      <Box data-qaid="event-page-first-column-children">
        {enabledForEventOrganizedAs(event.eventOrganizedAs, 'smsSignup') && (
          <AuthFormContextProvider isCheckoutAuth={false}>
            <MultiStepSignUpContextProvider>
              <SmsSignUpForm
                disableClose
                formContext={`${dataQaid}-sms-sign-up`}
                backgroundColor={colors.accent.primaryAccent.lightest}
                title={formatMessage({
                  id: 'event.postPurchase.smsSignUp.title',
                })}
                description={formatMessage({
                  id: 'event.postPurchase.smsSignUp.description',
                })}
                p={4}
                checkSmsPopupCookie={false}
              />
            </MultiStepSignUpContextProvider>
          </AuthFormContextProvider>
        )}
        <AttendeeDetailsForm
          ticketPurchaserName={loggedInUser.firstName}
          eventAttendee={eventAttendee}
        />
        <EventCTAWidget
          event={event}
          eventAttendeeExists={true}
          onClickGetTickets={openCheckoutDrawer}
          isDrawerOpen={displayCheckoutSection}
          isBuyTicketsCTAEnabled={canPurchaseMoreTickets}
          maxTicketsBought={maxTicketsBought}
        />

        {displayAdditionalPurchaseConfirmationModal && (
          <AdditionalPurchaseConfirmationModal
            isShowing={displayAdditionalPurchaseConfirmationModal}
            setIsShowing={setDisplayAdditionalPurchaseConfirmationModal}
            confirmationAction={() => setDisplayCheckoutSection(true)}
          />
        )}

        <Responsive hideUp="smallDesktop">
          <Drawer
            isOpen={displayCheckoutSection}
            setIsOpen={setDisplayCheckoutSection}
            onClose={onDrawerClose}
          >
            <CheckoutWidget
              alreadyConfirmedAdditionalPurchase={true}
              allowPromoCode={false}
              disabled={!canPurchaseMoreTickets}
            />
          </Drawer>
        </Responsive>
      </Box>
    </EventPageTemplate>
  );
};
